import * as React from 'react';
import { Tag, Plus, Check } from 'lucide-react';

import { cn } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './dialog';
import { Button } from './button';
import { Input } from './input';
import { ScrollArea } from './scroll-area';
import { TagBadge } from './tag-badge';
import { TagColorPicker, DEFAULT_TAG_COLOR } from './tag-color-picker';

export interface TagSelectorItem {
  id: string;
  name: string;
  color?: string | null;
  count?: number;
}

export interface CreateTagOptions {
  name: string;
  color: string;
}

interface TagSelectorDialogProps {
  /** Dialog open state */
  open: boolean;
  /** Called when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** Dialog title */
  title: string;
  /** Dialog description */
  description?: string;
  /** Available items to select from */
  items: TagSelectorItem[];
  /** Currently selected item IDs */
  selectedIds: string[];
  /** Called when selection changes */
  onSelectionChange: (ids: string[]) => void;
  /** Whether to show color indicators */
  showColors?: boolean;
  /** Whether to allow creating new items inline */
  allowCreate?: boolean;
  /** Called when user wants to create a new item (returns new item ID) */
  onCreate?: (options: CreateTagOptions) => Promise<string | undefined>;
  /** Placeholder for create input */
  createPlaceholder?: string;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Called when user clicks save */
  onSave: () => void;
  /** Loading state for items */
  isLoading?: boolean;
}

export function TagSelectorDialog({
  open,
  onOpenChange,
  title,
  description,
  items,
  selectedIds,
  onSelectionChange,
  showColors = false,
  allowCreate = false,
  onCreate,
  createPlaceholder = 'New tag name...',
  isSaving = false,
  onSave,
  isLoading = false,
}: TagSelectorDialogProps) {
  const [createInput, setCreateInput] = React.useState('');
  const [createColor, setCreateColor] = React.useState(DEFAULT_TAG_COLOR);
  const [isCreating, setIsCreating] = React.useState(false);
  const [showCreateForm, setShowCreateForm] = React.useState(false);

  const selectedItems = React.useMemo(
    () => items.filter((item) => selectedIds.includes(item.id)),
    [items, selectedIds]
  );

  const availableItems = React.useMemo(
    () => items.filter((item) => !selectedIds.includes(item.id)),
    [items, selectedIds]
  );

  const handleToggleItem = (itemId: string) => {
    if (selectedIds.includes(itemId)) {
      onSelectionChange(selectedIds.filter((id) => id !== itemId));
    } else {
      onSelectionChange([...selectedIds, itemId]);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    onSelectionChange(selectedIds.filter((id) => id !== itemId));
  };

  const handleCreateItem = async () => {
    if (!createInput.trim() || !onCreate) return;

    setIsCreating(true);
    try {
      const newItemId = await onCreate({
        name: createInput.trim(),
        color: createColor,
      });
      if (newItemId) {
        onSelectionChange([...selectedIds, newItemId]);
        setCreateInput('');
        setCreateColor(DEFAULT_TAG_COLOR);
        setShowCreateForm(false);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateItem();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowCreateForm(false);
      setCreateInput('');
    }
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setCreateInput('');
    setCreateColor(DEFAULT_TAG_COLOR);
  };

  // Reset form when dialog closes
  React.useEffect(() => {
    if (!open) {
      setShowCreateForm(false);
      setCreateInput('');
      setCreateColor(DEFAULT_TAG_COLOR);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="size-5 text-muted-foreground" />
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* Selected Tags */}
          {selectedItems.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">Selected</div>
              <div className="flex flex-wrap gap-1.5">
                {selectedItems.map((item) => (
                  <TagBadge
                    key={item.id}
                    name={item.name}
                    color={showColors ? item.color : undefined}
                    removable
                    onRemove={() => handleRemoveItem(item.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Available Tags List */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">
              {selectedItems.length > 0 ? 'Available' : 'Tags'}
            </div>
            <ScrollArea className="h-[180px] rounded-md border">
              {isLoading ? (
                <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                  Loading...
                </div>
              ) : availableItems.length === 0 && !allowCreate ? (
                <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                  No tags available
                </div>
              ) : (
                <div className="p-1.5 space-y-0.5">
                  {availableItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleToggleItem(item.id)}
                      className={cn(
                        'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm transition-colors',
                        'hover:bg-accent hover:text-accent-foreground',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                      )}
                    >
                      {showColors && item.color && (
                        <div
                          className="size-3 rounded-full shrink-0"
                          style={{ backgroundColor: item.color }}
                        />
                      )}
                      {!showColors && <Tag className="size-3.5 shrink-0 text-muted-foreground" />}
                      <span className="flex-1 text-left truncate">{item.name}</span>
                      {typeof item.count === 'number' && (
                        <span className="text-xs text-muted-foreground shrink-0">{item.count}</span>
                      )}
                    </button>
                  ))}
                  {availableItems.length === 0 && allowCreate && (
                    <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                      No tags yet. Create one below.
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Create New Tag */}
          {allowCreate && onCreate && (
            <div className="space-y-2">
              {!showCreateForm ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setShowCreateForm(true)}
                >
                  <Plus className="size-4 mr-1.5" />
                  Create new tag
                </Button>
              ) : (
                <div className="rounded-md border p-3 space-y-3 bg-muted/30">
                  <div className="flex gap-2">
                    <div
                      className="size-9 rounded-md border flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${createColor}20`, borderColor: createColor }}
                    >
                      <Tag className="size-4" style={{ color: createColor }} />
                    </div>
                    <Input
                      placeholder={createPlaceholder}
                      value={createInput}
                      onChange={(e) => setCreateInput(e.target.value)}
                      onKeyDown={handleCreateInputKeyDown}
                      disabled={isCreating}
                      autoFocus
                      className="h-9"
                    />
                  </div>
                  <TagColorPicker value={createColor} onChange={setCreateColor} size="sm" />
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelCreate}
                      disabled={isCreating}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateItem}
                      disabled={!createInput.trim() || isCreating}
                    >
                      {isCreating ? (
                        'Creating...'
                      ) : (
                        <>
                          <Check className="size-4 mr-1" />
                          Create
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="button" onClick={onSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
